import React, { useState } from "react";
import RailGroup from "./RailGroup";
import { dealSummary, feeLines } from "./moneyModel.mjs";
import { askParts, asSentence, dealRowsShown, dealEmptySummary, parseMoney, railSituation } from "./railModel.mjs";

/*
 * DEAL AND MONEY, labelled honestly (frames 1a, 1b, 1d; the matrix's Deal row).
 *
 * Fixed rows, each saying where it came from (record.deal, built once by dealRows
 * in record/moneyModel.mjs): their ad budget (theirs; on a client "carried from the
 * deal", with "Is this our retainer?" asked), what we quoted (kept at every stage
 * after it went), the fee, anything negotiated, the agreement link, won on. This
 * file never relabels a row: monthly_value is their budget until Gabriel says
 * otherwise, whatever the status, because a number that changed its name when the
 * company was won is how Pluto read "Retainer $10K/mo" the morning after.
 *
 * A ROW POINTS AT THE LEAD BLOCK INSTEAD OF REPEATING IT. Where the lead block is
 * the fee (New client, Client, Churned), the Fee row says how it stands and links
 * "in New client" up to it: one fee on the screen, never two copies that can drift.
 *
 * ONE EDIT PER CARD (the brief). It opens one editor holding every money field the
 * rows show: the budget, the fee, the note, the agreement link. "Is this our
 * retainer? Edit" and each row's "Add" open the same editor on their own field.
 * The fields and their write are SignedTerms' (PATCH /companies/:id, an emptied
 * field sent as null so clearing actually clears), rebuilt in the record's grammar
 * rather than mounted: SignedTerms renders only on a won company (Northwind needs
 * the fee at Agreement + legal), and draws its own yellow Save, which on this page
 * would be a second yellow button.
 *
 * The agreement link with nothing pasted is "PandaDoc notification email ↗": a
 * mail ABOUT the document, called that, never "Signed agreement".
 *
 * Props:
 *   record   the shell's bundle: deal (rows), company, key (the situation)
 *   ops      ops.patchCompany(body) → promise (reloads and tells the host)
 *   state    "open" | "summary" (the matrix cell): whether it starts open
 *   sheet    boolean, phone: the group opens as a bottom sheet
 */

// "in New client ↑": the fee is up there, so the link goes up there.
function jumpToLeadBlock(event) {
  const root = event.currentTarget.closest(".rec") || document;
  const block = root.querySelector("#rec-lead-block");
  if (block && block.scrollIntoView) block.scrollIntoView({ behavior: "smooth", block: "start" });
}

const FIELDS = [
  { key: "monthly_value", label: "Their monthly ad budget", kind: "money", placeholder: "40000" },
  { key: "signed_fee_structure", label: "Fee", kind: "lines", placeholder: "$3,500/mo base\n15% of spend $10K–$50K", hint: "One line per part of the deal." },
  { key: "signed_terms_note", label: "Anything they negotiated", kind: "text" },
  { key: "signed_agreement_url", label: "Agreement link", kind: "text", placeholder: "Paste a Drive or PandaDoc document link", hint: "Empty falls back to the PandaDoc notification email, which is labelled as such." },
];

function valueOf(company, key) {
  const v = company ? company[key] : null;
  if (v === null || v === undefined) return "";
  return String(v);
}

function DealEditor({ company, focus, isClient, ops, onClose }) {
  const [form, setForm] = useState(() => Object.fromEntries(FIELDS.map((f) => [f.key, valueOf(company, f.key)])));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    // Only what changed goes up: the budget and the terms are separate facts, and
    // re-sending an unchanged fee would stamp updated_at on a field nobody touched.
    const body = {};
    for (const f of FIELDS) {
      const now = form[f.key].trim();
      if (now === valueOf(company, f.key).trim()) continue;
      if (f.kind === "money") {
        if (!now) body[f.key] = null;
        else {
          const n = parseMoney(now);
          if (n === null) {
            setError("Their budget should be a number, like 40000 or $40K.");
            return;
          }
          body[f.key] = n;
        }
      } else body[f.key] = now || null;
    }
    if (!Object.keys(body).length) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await ops.patchCompany(body);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rec-dealedit">
      {FIELDS.map((f) => {
        const id = `rec-deal-${f.key}`;
        const common = {
          id,
          value: form[f.key],
          disabled: busy,
          autoFocus: focus === f.key,
          placeholder: f.placeholder,
          onChange: (e) => setForm({ ...form, [f.key]: e.target.value }),
        };
        return (
          <div className="rec-field" key={f.key}>
            <label className="rec-field__label" htmlFor={id}>{f.label}</label>
            {f.kind === "lines" ? (
              <textarea className="rec-textarea" rows={3} {...common} />
            ) : (
              <input className="rec-input" inputMode={f.kind === "money" ? "decimal" : undefined} {...common} />
            )}
            {f.key === "monthly_value" && isClient && (
              <span className="rec-field__hint">On a client this field is read as our retainer.</span>
            )}
            {f.hint && <span className="rec-field__hint">{f.hint}</span>}
          </div>
        );
      })}
      <div className="rec-actionsrow">
        <button type="button" className="rec-btn rec-btn--dark rec-btn--sm" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>
        <button type="button" className="rec-btn rec-btn--plain rec-btn--sm" disabled={busy} onClick={onClose}>Cancel</button>
        {error && <span className="rec-err">{error}</span>}
      </div>
    </div>
  );
}

function Empty({ row, onAdd, hint = null }) {
  return (
    <div className="rec-empty">
      not recorded
      {row.add && (
        <>
          {" · "}
          <button type="button" className="rec-link" onClick={() => onAdd(row.add.field)}>Add</button>
        </>
      )}
      {hint && <span className="rec-row__hint"> · {hint}</span>}
    </div>
  );
}

function Row({ row, company, onEdit }) {
  const isClient = company && (company.status === "client" || company.status === "churned");
  if (row.key === "budget") {
    const ask = askParts(row.ask);
    return (
      <div className="rec-row">
        <div className="rec-row__label">{row.label}</div>
        {row.empty ? (
          <Empty row={row} onAdd={onEdit} />
        ) : (
          <>
            <div className="rec-row__value">{row.value}</div>
            {ask ? (
              <div className="rec-row__sub">
                {row.sub ? `${asSentence(row.sub)} ` : ""}
                {ask.lead ? `${ask.lead} ` : ""}
                {ask.question && <b className="rec-strong">{ask.question}</b>}{" "}
                <button type="button" className="rec-link" onClick={() => onEdit("monthly_value")}>Edit</button>
              </div>
            ) : (
              row.sub && <div className="rec-row__meta">{row.sub}</div>
            )}
            {row.proposed && (
              <div className="rec-row__proposed">
                {row.proposed}
                <span className="rec-proposed">Proposed</span>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  if (row.key === "quoted") {
    return (
      <div className="rec-row">
        <div className="rec-row__label">{row.label}</div>
        {row.empty ? (
          <Empty row={row} onAdd={onEdit} />
        ) : (
          <>
            {/* The quote itself is the deal's number, so it reads bold; "In the
                agreement · Sep 27" is a pointer, so it does not. */}
            <div className={row.quote ? "rec-row__value" : "rec-row__value rec-row__value--plain"}>{row.value}</div>
            {row.sub && <div className="rec-row__meta">{row.sub}</div>}
          </>
        )}
      </div>
    );
  }

  if (row.key === "fee") {
    const lines = row.lines && row.lines.length ? row.lines : row.value ? feeLines(row.value) : [];
    return (
      <div className="rec-row">
        <div className="rec-row__label">{row.label}</div>
        {row.pointer ? (
          <div className="rec-row__note">
            {row.value}{" "}
            <button type="button" className="rec-link" onClick={jumpToLeadBlock}>{row.pointer.label}</button>
          </div>
        ) : row.empty ? (
          <Empty row={row} onAdd={onEdit} />
        ) : (
          <div className="rec-row__lines">
            {lines.map((l, i) => (
              <span key={i}>{l}</span>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (row.key === "negotiated") {
    return (
      <div className="rec-row">
        <div className="rec-row__label">{row.label}</div>
        {row.empty ? <Empty row={row} onAdd={onEdit} /> : <div className="rec-row__note">{row.value}</div>}
      </div>
    );
  }

  if (row.key === "agreement") {
    // A won company with nothing on record says so in the words the record can
    // stand behind (reality.txt [1]): no signed copy ON RECORD, never "no email
    // arrived", because a signed notice after a hand-marked win leaves no trace.
    const hint = row.empty && company && company.won_at ? "no signed copy on record" : null;
    return (
      <div className="rec-row">
        <div className="rec-row__label">{row.label}</div>
        {row.link ? (
          <a className="rec-row__link" href={row.link.url} target="_blank" rel="noopener noreferrer">{row.link.label}</a>
        ) : (
          <Empty row={row} onAdd={onEdit} hint={hint} />
        )}
      </div>
    );
  }

  // Won on: "Sep 30 · set by you", or "not won yet" in the quiet grey.
  const won = company && company.won_at;
  return (
    <div className="rec-row">
      <div className="rec-row__label">{row.label}</div>
      <div className={won ? "" : "rec-empty"}>{row.value || (isClient ? "not recorded" : "not won yet")}</div>
    </div>
  );
}

export default function DealMoney({ record, ops, state = "summary", sheet = false }) {
  // null: the rows; a field name: the editor open with that field focused.
  const [editing, setEditing] = useState(null);
  const company = record.company;
  const situation = railSituation(record.view);
  const rows = dealRowsShown(record.deal || [], situation);
  const isClient = !!company && (company.status === "client" || company.status === "churned");
  const summary = dealSummary(record.deal || []) || dealEmptySummary(situation);
  const edit = (field) => setEditing(field || "monthly_value");

  return (
    <RailGroup
      id="deal"
      title="Deal and money"
      summary={summary}
      defaultOpen={state === "open"}
      sheet={sheet}
      edit={company && !editing ? { label: "Edit", onClick: () => edit("monthly_value") } : null}
    >
      {editing && company ? (
        <DealEditor key={editing} company={company} focus={editing} isClient={isClient} ops={ops} onClose={() => setEditing(null)} />
      ) : (
        <div className="rec-rows">
          {rows.map((r) => (
            <Row key={r.key} row={r} company={company} onEdit={edit} />
          ))}
        </div>
      )}
    </RailGroup>
  );
}
